import { sleep } from 'radash';

// 基础接口定义,包含通用的位置和尺寸属性
interface ICommon {
    x: number; // x坐标
    y: number; // y坐标
    width: number; // 宽度
    height: number; // 高度
    isGroup?: boolean; // 是否为组内元素（纵向）
    isXGroup?: boolean; // 是否为横向组内元素
    isCircle?: boolean; // 是否绘制为圆形
}

// 图片绘制接口
interface IImg extends ICommon {
    src: string; // 图片源地址
    fallbackSrc?: string; // 图片加载失败时兜底图地址
    isCircle?: boolean; // 是否绘制为圆形
    radius?: number | number[]; // 圆角半径，支持单个值或数组 [左上, 右上, 右下, 左下]
}

// 渐变相关接口
interface IGradient {
    type: ''; // 渐变类型
    direction: number[]; // 渐变方向坐标数组
    colorList: any[][]; // 渐变色停止点列表
}

// 矩形绘制接口
interface IRect extends ICommon {
    fillStyle: string; // 填充样式
    gradient: IGradient; // 渐变配置
    radius?: number | number[]; // 圆角半径
}

// 线条绘制接口
interface ILine {
    strokeStyle: string; // 线条样式
    x1: number; // 起点x坐标
    y1: number; // 起点y坐标
    x2: number; // 终点x坐标
    y2: number; // 终点y坐标
    lineDash?: number[]; // 虚线样式，例如 [5, 5] 表示5px实线，5px空隙
    lineWidth?: number; // 线条宽度
}

// 文本绘制接口
interface IText extends Omit<ICommon, 'height'> {
    text: string | string[]; // 文本内容
    font: string; // 字体样式
    fillStyle: string; // 填充颜色
    lineHeight: number; // 行高
    letterSpacing?: number; // 字间距（可选，默认为0）
    align?: CanvasTextAlign; // 对齐方式
    baseline?: CanvasTextBaseline; // 基线对齐方式
    isMultiline?: boolean; // 是否多行
    ellipsis?: boolean; // 是否显示省略号
    suffix?: string; // 省略号样式
}

// 文本片段：文本 + 样式
interface ITextSegment {
    text: string;
    font: string;
    fillStyle: string;
    letterSpacing?: number;
    baseline?: CanvasTextBaseline;
}

// 混合样式多行文本接口
interface IMixedText extends Omit<ICommon, 'height'> {
    segments: ITextSegment[];
    lineHeight: number;
    align?: CanvasTextAlign;
    baseline?: CanvasTextBaseline;
    ellipsis?: boolean;
    suffix?: string;
    maxLines?: number;
}

// 配置项接口
export interface IConfig {
    type: 'img' | 'text' | 'rect' | 'line' | 'group' | 'xgroup' | 'mixedText'; // 绘制类型
    [key: string]: any; // 其他配置项
}

export class DrawCanvas {
    canvas: HTMLCanvasElement;
    width: number;
    height: number;
    ctx: CanvasRenderingContext2D;
    scaleRatio: number;
    groupY = 0; // 组内元素的y坐标偏移量
    groupX = 0; // 横向组内元素的x坐标偏移量
    isFinished = false; // 是否完成绘制
    drawingSteps: Array<{ type: string; status: 'pending' | 'loading' | 'completed' | 'error'; startTime?: number; endTime?: number; data?: any; error?: any }> = []; // 绘制步骤记录
    currentStepIndex = 0; // 当前步骤索引
    createImageStartTime = 0; // 记录 createImage 开始时间
    supportsLetterSpacing = false; // 是否支持 letterSpacing 属性

    // 构造函数,初始化画布
    constructor(canvas: HTMLCanvasElement, config?: IConfig[], options?: { scale?: number } | number) {
        this.width = canvas.width;
        this.height = canvas.height;
        this.canvas = canvas;
        this.ctx = canvas.getContext('2d')!;
        const scale = typeof options === 'number' ? options : options?.scale;
        this.scaleRatio = Number.isFinite(scale as number) && (scale as number) > 0 ? (scale as number) : 2;
        this.ctx.scale(this.scaleRatio, this.scaleRatio);
        // 检测是否支持 letterSpacing (Chrome 99+)
        this.supportsLetterSpacing = 'letterSpacing' in this.ctx;

        // 检测 TextMetrics 支持情况
        const testMetrics = this.ctx.measureText('test');
        const supportsAdvancedMetrics = testMetrics.actualBoundingBoxAscent !== undefined;

        console.log('Canvas 兼容性检测:', {
            supportsLetterSpacing: this.supportsLetterSpacing,
            supportsAdvancedMetrics: supportsAdvancedMetrics,
            userAgent: navigator.userAgent,
        });

        config && this.init(config);
    }

    // 初始化绘制
    async init(config: IConfig[], isGroup = false, isXGroup = false) {
        if (isGroup && this.groupY === 0) {
            this.groupY = config[0].y;
        }
        if (isXGroup) {
            // 每次进入新的 xgroup 时重置 groupX 为 0，第一个元素会设置正确的起始位置
            this.groupX = 0;
        }
        for (const item of config) {
            item.isGroup = isGroup;
            item.isXGroup = isXGroup;

            // 记录步骤开始
            const stepIndex = this.currentStepIndex++;
            this.drawingSteps[stepIndex] = {
                type: item.type,
                status: 'pending',
                startTime: Date.now(),
                data: item.type === 'img' ? { src: (item as any).src, } : { text: (item as any).text, },
            };

            try {
                this.drawingSteps[stepIndex].status = 'loading';
                switch (item.type) {
                    case 'img':
                        await this.drawImage(item as any);
                        break;
                    case 'text':
                        this.drawText(item as any);
                        break;
                    case 'rect':
                        this.fillRect(item as any);
                        break;
                    case 'line':
                        this.drawLine(item as any);
                        break;
                    case 'mixedText':
                        this.drawMixedText(item as any);
                        break;
                    case 'group':
                        await this.init(item.children, true, false);
                        break;
                    case 'xgroup':
                        await this.init(item.children, false, true);
                        break;
                    default:
                        break;
                }
                this.drawingSteps[stepIndex].status = 'completed';
                this.drawingSteps[stepIndex].endTime = Date.now();
            } catch (error) {
                this.drawingSteps[stepIndex].status = 'error';
                this.drawingSteps[stepIndex].error = error;
                this.drawingSteps[stepIndex].endTime = Date.now();
                console.error(`绘制步骤 ${stepIndex} (${item.type}) 失败:`, error, item);
            }
        }
        if (!isGroup && !isXGroup) {
            setTimeout(() => {
                this.isFinished = true;
            }, 1000);
        }
    }

    // 更新组信息
    updateGroupInfo({ y, x, }: { y?: number; x?: number }) {
        if (y !== undefined) {
            this.groupY = y;
        }
        if (x !== undefined) {
            this.groupX = x;
        }
    }

    // 绘制图片
    drawImage({ src, fallbackSrc, x, y, width, height, isGroup, isXGroup, isCircle, radius, }: IImg): Promise<void> {
        const loadStartTime = Date.now();
        return new Promise((resolve) => {
            // 对于 xgroup，先计算并更新 groupX，确保顺序正确
            const newY = isGroup ? this.groupY : y;
            const newX = isXGroup ? this.groupX + x : x;

            // 先更新 groupX，避免异步加载导致的顺序问题
            if (isXGroup) {
                this.groupX = newX + width;
            }

            const sourceQueue = [src, fallbackSrc].filter(Boolean) as string[];
            let settled = false;

            const safeResolve = () => {
                if (settled) return;
                settled = true;
                resolve();
            };

            const setImageSrc = (img: HTMLImageElement, imageSrc: string) => {
                // 判断是否为 base64 图片
                if (imageSrc.startsWith('data:image')) {
                    img.src = imageSrc;
                    return;
                }
                const hasHash = imageSrc.includes('?');
                img.src = imageSrc + (hasHash ? '&' : '?') + 'v=poster';
            };

            const drawLoadedImage = (img: HTMLImageElement, imageSrc: string) => {
                const loadTime = Date.now() - loadStartTime;
                if (loadTime > 1000) {
                    console.warn(`图片加载较慢 (${loadTime}ms):`, imageSrc);
                }

                try {
                    // 如果设置了圆形或圆角，需要进行裁剪
                    if (isCircle || radius) {
                        // 保存当前环境的状态
                        this.ctx.save();
                        // 开始新的路径
                        this.ctx.beginPath();

                        if (isCircle) {
                            // 创建圆形裁剪路径
                            const circleRadius = width / 2;
                            const centerX = newX + circleRadius;
                            const centerY = newY + circleRadius;
                            this.ctx.arc(centerX, centerY, circleRadius, 0, Math.PI * 2, true);
                        } else if (radius) {
                            // 创建圆角矩形裁剪路径
                            if (Array.isArray(radius)) {
                                // 支持四个角不同的圆角
                                const [tl, tr, br, bl] = radius.concat([0, 0, 0, 0]).slice(0, 4);
                                this.ctx.moveTo(newX + tl, newY);
                                this.ctx.lineTo(newX + width - tr, newY);
                                this.ctx.arcTo(newX + width, newY, newX + width, newY + tr, tr);
                                this.ctx.lineTo(newX + width, newY + height - br);
                                this.ctx.arcTo(newX + width, newY + height, newX + width - br, newY + height, br);
                                this.ctx.lineTo(newX + bl, newY + height);
                                this.ctx.arcTo(newX, newY + height, newX, newY + height - bl, bl);
                                this.ctx.lineTo(newX, newY + tl);
                                this.ctx.arcTo(newX, newY, newX + tl, newY, tl);
                            } else {
                                // 四个角相同的圆角
                                this.ctx.moveTo(newX + radius, newY);
                                this.ctx.lineTo(newX + width - radius, newY);
                                this.ctx.arcTo(newX + width, newY, newX + width, newY + radius, radius);
                                this.ctx.lineTo(newX + width, newY + height - radius);
                                this.ctx.arcTo(newX + width, newY + height, newX + width - radius, newY + height, radius);
                                this.ctx.lineTo(newX + radius, newY + height);
                                this.ctx.arcTo(newX, newY + height, newX, newY + height - radius, radius);
                                this.ctx.lineTo(newX, newY + radius);
                                this.ctx.arcTo(newX, newY, newX + radius, newY, radius);
                            }
                        }

                        this.ctx.closePath();
                        // 裁剪
                        this.ctx.clip();
                    }

                    this.ctx.drawImage(img, newX, newY, width, height);

                    if (isCircle || radius) {
                        // 恢复到之前保存的状态
                        this.ctx.restore();
                    }

                    // group 的 y 坐标更新在绘制完成后
                    isGroup && this.updateGroupInfo({ y: newY + height, });
                } catch (error) {
                    console.error('图片绘制失败:', imageSrc, error);
                } finally {
                    safeResolve();
                }
            };

            const tryLoadImage = (queueIndex: number) => {
                const currentSrc = sourceQueue[queueIndex];
                if (!currentSrc) {
                    safeResolve(); // 没有可用图片源时直接跳过
                    return;
                }

                const img = new Image();
                img.crossOrigin = 'Anonymous';
                let attemptFinished = false;

                const finishAttempt = () => {
                    if (attemptFinished || settled) return false;
                    attemptFinished = true;
                    return true;
                };

                // 添加图片加载超时检测（10秒）
                const imgLoadTimeout = setTimeout(() => {
                    if (!finishAttempt()) return;
                    const loadTime = Date.now() - loadStartTime;
                    console.error(`图片加载超时 (${loadTime}ms):`, {
                        src: currentSrc,
                        position: { x: newX, y: newY, },
                        size: { width, height, },
                        isGroup,
                        isXGroup,
                    });

                    if (queueIndex + 1 < sourceQueue.length) {
                        console.warn('主图超时，尝试使用兜底图:', sourceQueue[queueIndex + 1]);
                        tryLoadImage(queueIndex + 1);
                    } else {
                        safeResolve(); // 兜底图也失败，继续执行
                    }
                }, 10000);

                img.onload = () => {
                    if (!finishAttempt()) return;
                    clearTimeout(imgLoadTimeout);
                    drawLoadedImage(img, currentSrc);
                };

                img.onerror = (err) => {
                    if (!finishAttempt()) return;
                    clearTimeout(imgLoadTimeout);
                    console.error('图片加载失败:', {
                        src: currentSrc,
                        error: err,
                        position: { x: newX, y: newY, },
                        size: { width, height, },
                    });

                    if (queueIndex + 1 < sourceQueue.length) {
                        console.warn('主图加载失败，尝试使用兜底图:', sourceQueue[queueIndex + 1]);
                        tryLoadImage(queueIndex + 1);
                    } else {
                        safeResolve(); // 兜底图也失败，再跳过
                    }
                };

                setImageSrc(img, currentSrc);
            };

            tryLoadImage(0);
        });
    }

    // 绘制两端对齐的单行文本
    drawJustifiedLine(text: string, x: number, y: number, width: number, letterSpacing: number = 0) {
        const words = text.split('');

        // 如果不支持原生 letterSpacing 且需要字间距，手动实现
        if (!this.supportsLetterSpacing && letterSpacing && letterSpacing !== 0) {
            let xPosition = x;
            words.forEach((word, index) => {
                this.ctx.fillText(word, xPosition, y);
                if (index < words.length - 1) {
                    xPosition += this.ctx.measureText(word).width + letterSpacing;
                }
            });
            return;
        }

        const lineWidth = this.ctx.measureText(text).width;
        const oneWordWidth = this.ctx.measureText(words[0]).width;
        let gap = (width - lineWidth) / (words.length - 1);
        // 大于1.5个字，不两端对齐
        if (width - lineWidth > oneWordWidth * 1.5) {
            gap = 0;
        }
        let xPosition = x;
        words.forEach((word, index) => {
            this.ctx.fillText(word, xPosition, y);
            if (index < words.length - 1) {
                xPosition += this.ctx.measureText(word).width + gap;
            }
        });
    }

    // 绘制两端对齐的多行文本
    drawJustifiedText(text: string, x: IText['x'], y: IText['y'], width: IText['width'], lineHeight: IText['lineHeight'], _height: number, letterSpacing: number = 0) {
        const words = text.split('');
        let line = '';
        let yPosition = y;
        for (let n = 0; n < words.length; n++) {
            const testLine = line + words[n];
            const metrics = this.ctx.measureText(testLine);
            const testWidth = metrics.width;
            if (testWidth > width && n > 0) {
                this.drawJustifiedLine(line.trim(), x, yPosition, width, letterSpacing);
                line = words[n];
                yPosition += lineHeight;
            } else {
                line = testLine;
            }
        }
        this.drawJustifiedLine(line.trim(), x, yPosition, width, letterSpacing);
        return yPosition + lineHeight;
    }

    // 按宽度拆分文本行（保留字符顺序，不做两端对齐）
    splitTextToLines(text: string, width: number, letterSpacing: number = 0): string[] {
        const words = text.split('');
        const lines: string[] = [];
        let line = '';
        for (let i = 0; i < words.length; i++) {
            const testLine = line + words[i];
            const testWidth = this.getTextWidth({
                text: testLine,
                font: this.ctx.font,
                fillStyle: this.ctx.fillStyle as string,
                x: 0,
                y: 0,
                width,
                lineHeight: 0,
                letterSpacing,
            } as IText);
            if (testWidth > width && line) {
                lines.push(line);
                line = words[i];
            } else {
                line = testLine;
            }
        }
        if (line) {
            lines.push(line);
        }
        return lines.length ? lines : [''];
    }

    // 绘制按对齐方式自动换行文本（支持 center/right）
    drawWrappedText(text: string, x: IText['x'], y: IText['y'], width: IText['width'], lineHeight: IText['lineHeight'], letterSpacing: number = 0, ellipsis = false, suffix = '...') {
        const lines = this.splitTextToLines(text, width, letterSpacing);
        let yPosition = y;
        lines.forEach((line, index) => {
            const isLastLine = index === lines.length - 1;
            const result = this.drawTextSingleLine({
                text: line,
                x,
                y: yPosition,
                lineHeight,
                width,
                ellipsis: ellipsis && isLastLine,
                suffix,
                font: this.ctx.font,
                baseline: this.ctx.textBaseline,
                letterSpacing,
            });
            yPosition = result.y;
        });
        return yPosition;
    }

    // 计算单个文本的高度
    getSingleTextHeight(text: string, width: number, lineHeight: number, letterSpacing: number = 0) {
        const _words = text.split('');
        let _lines = 0;
        let _lineW = 0;
        _words.forEach((item, index) => {
            const _w = this.ctx.measureText(item).width;
            _lineW += _w;
            // 如果不支持原生 letterSpacing，需要手动加上字间距
            if (!this.supportsLetterSpacing && letterSpacing !== 0 && index < _words.length - 1) {
                _lineW += letterSpacing;
            }
            if (_lineW > width) {
                _lines++;
                _lineW = 0;
            }
        });
        if (_lineW > 0) {
            _lines++;
        }
        return _lines * lineHeight;
    }

    // 获取文本总高度
    getTextHeight(options: IText) {
        this.ctx.font = options.font;
        this.ctx.fillStyle = options.fillStyle;
        const spacing = options.letterSpacing || 0;
        // 只在支持的情况下设置 letterSpacing
        if (this.supportsLetterSpacing) {
            (this.ctx as any).letterSpacing = `${spacing}px`;
        }
        let h = 0;
        if (Array.isArray(options.text)) {
            options.text.forEach((item) => {
                h += this.getSingleTextHeight(item, options.width, options.lineHeight, spacing);
            });
        } else {
            h = this.getSingleTextHeight(options.text, options.width, options.lineHeight, spacing);
        }
        return h;
    }

    // 获取文本宽度
    getTextWidth(options: IText): number {
        this.ctx.font = options.font;
        // 只在支持的情况下设置 letterSpacing
        if (this.supportsLetterSpacing) {
            (this.ctx as any).letterSpacing = `${options.letterSpacing}px`;
        }

        const calculateWidth = (text: string): number => {
            const baseWidth = this.ctx.measureText(text).width;
            // 如果不支持原生 letterSpacing，手动计算宽度
            const spacing = options.letterSpacing || 0;
            if (!this.supportsLetterSpacing && spacing !== 0) {
                return baseWidth + (text.length - 1) * spacing;
            }
            return baseWidth;
        };

        if (Array.isArray(options.text)) {
            return Math.max(...options.text.map((t) => calculateWidth(t)));
        }
        return calculateWidth(options.text);
    }

    getMixedTextWidth(segments: ITextSegment[]): number {
        let totalWidth = 0;
        for (const seg of segments) {
            const spacing = seg.letterSpacing || 0;
            this.ctx.font = seg.font;
            if (this.supportsLetterSpacing) {
                (this.ctx as any).letterSpacing = `${spacing}px`;
            }
            const baseWidth = this.ctx.measureText(seg.text).width;
            if (!this.supportsLetterSpacing && spacing !== 0) {
                totalWidth += baseWidth + (seg.text.length - 1) * spacing;
            } else {
                totalWidth += baseWidth;
            }
        }
        return totalWidth;
    }

    // 获取文本度量信息（用于精确计算文本尺寸）
    getTextMetrics(text: string, font: string, letterSpacing: number = 0): TextMetrics {
        this.ctx.font = font;
        // 只在支持的情况下设置 letterSpacing
        if (this.supportsLetterSpacing) {
            (this.ctx as any).letterSpacing = `${letterSpacing}px`;
        }
        return this.ctx.measureText(text);
    }

    // 根据 lineHeight 精确计算单行文本的 y 值
    // 当 baseline 为 'top' 时，按顶部对齐直接使用传入 y
    calculateTextY(text: string, font: string, y: number, lineHeight: number, baseline: CanvasTextBaseline = 'top', letterSpacing: number = 0): number {
        const metrics = this.getTextMetrics(text, font, letterSpacing);

        // Chrome 55 不支持 actualBoundingBoxAscent/Descent，需要降级处理
        if (metrics.actualBoundingBoxAscent === undefined || metrics.actualBoundingBoxDescent === undefined) {
            // 降级方案：直接使用 baseline 的默认行为
            switch (baseline) {
                case 'top':
                    return y;
                case 'middle':
                    return y + lineHeight / 2;
                case 'bottom':
                    return y + lineHeight;
                case 'alphabetic':
                    return y + lineHeight * 0.8; // 粗略估计
                default:
                    return y;
            }
        }

        // 文本实际高度
        const actualHeight = metrics.actualBoundingBoxAscent + metrics.actualBoundingBoxDescent;

        switch (baseline) {
            case 'top':
                return y;
            case 'middle':
                // baseline='middle' 时，y 是文本中心，直接加上 lineHeight / 2
                return y + lineHeight / 2;
            case 'bottom':
                // baseline='bottom' 时，y 是文本底部
                return y + lineHeight - (lineHeight - actualHeight) / 2 - metrics.actualBoundingBoxDescent;
            case 'alphabetic':
                // baseline='alphabetic' 时，y 是字母基线
                return y + (lineHeight - actualHeight) / 2 + metrics.actualBoundingBoxAscent;
            default:
                return y;
        }
    }

    // 绘制文本
    drawText({ text, font, fillStyle, align = 'left', baseline = 'top', letterSpacing = 0, x, y, width, lineHeight, isGroup = false, isXGroup = false, isMultiline = false, ellipsis = false, suffix = '...', }: IText) {
        this.ctx.font = font;
        this.ctx.fillStyle = fillStyle;
        // 只在支持的情况下设置 letterSpacing
        if (this.supportsLetterSpacing) {
            (this.ctx as any).letterSpacing = `${letterSpacing}px`;
        }
        this.ctx.textBaseline = baseline;
        width = width || this.width;

        // 处理横向组合的 x 坐标
        const baseX = isXGroup ? this.groupX + x : x;
        let newX = baseX;

        if (align === 'center') {
            newX = width / 2 + baseX;
        } else if (align === 'right') {
            newX = width + baseX;
        }
        this.ctx.textAlign = align;
        let totalY = y;
        let textWidth = 0;

        if (!isMultiline) {
            const newY = isGroup ? this.groupY + y : y;
            const result = this.drawTextSingleLine({ text, x: newX, y: newY, lineHeight, width, ellipsis, suffix, font, baseline, letterSpacing, });
            totalY = result.y;
            // 使用实际渲染的文本宽度（包括省略号）
            textWidth = result.actualWidth;
        } else {
            const newY = isGroup ? this.groupY + y : y;
            if (Array.isArray(text)) {
                let currentY = newY;
                text.forEach((item) => {
                    const lineResult = this.drawWrappedText(item, newX, currentY, width, lineHeight, letterSpacing, ellipsis, suffix);
                    totalY = lineResult;
                    currentY = lineResult;
                });
            } else {
                if (align === 'left') {
                    totalY = this.drawJustifiedText(text, newX, newY, width, lineHeight, this.height, letterSpacing);
                } else {
                    totalY = this.drawWrappedText(text, newX, newY, width, lineHeight, letterSpacing, ellipsis, suffix);
                }
            }
            textWidth = width;
        }
        isGroup && this.updateGroupInfo({ y: totalY, });
        isXGroup && this.updateGroupInfo({ x: baseX + textWidth, });
        return totalY;
    }

    // 绘制单行文本
    drawTextSingleLine({ text, x, y, lineHeight, ellipsis, suffix, width, font, baseline, letterSpacing, }: Record<string, any>): { y: number; actualWidth: number } {
        let actualWidth = 0;
        if (ellipsis) {
            const _textArr = text.split('');
            let _totalWidth = 0;
            const _suffixW = this.ctx.measureText(suffix);
            for (let i = 0; i < _textArr.length; i++) {
                const _w = this.ctx.measureText(_textArr[i]);
                _totalWidth += _w.width;
                // 如果不支持原生 letterSpacing，手动加上字间距
                if (!this.supportsLetterSpacing && letterSpacing && i < _textArr.length - 1) {
                    _totalWidth += letterSpacing;
                }
                if (_totalWidth + _suffixW.width >= width) {
                    _textArr.splice(i);
                    text = _textArr.join('') + suffix;
                    break;
                }
            }
        }

        // 如果不支持原生 letterSpacing 且需要字间距，手动绘制每个字符
        if (!this.supportsLetterSpacing && letterSpacing && letterSpacing !== 0) {
            const chars = (text as string).split('');
            let xPosition = x;

            // 根据对齐方式调整起始位置
            if (this.ctx.textAlign === 'center') {
                const totalWidth = chars.reduce((sum, char, i) => {
                    return sum + this.ctx.measureText(char).width + (i < chars.length - 1 ? letterSpacing : 0);
                }, 0);
                xPosition = x - totalWidth / 2;
            } else if (this.ctx.textAlign === 'right') {
                const totalWidth = chars.reduce((sum, char, i) => {
                    return sum + this.ctx.measureText(char).width + (i < chars.length - 1 ? letterSpacing : 0);
                }, 0);
                xPosition = x - totalWidth;
            }

            // 临时设置为左对齐来逐字绘制
            const originalAlign = this.ctx.textAlign;
            this.ctx.textAlign = 'left';

            chars.forEach((char, index) => {
                // 精确计算 y 值
                let drawY = y;
                if (font && baseline && lineHeight) {
                    const metrics = this.getTextMetrics(char, font, 0);

                    // Chrome 55 不支持 actualBoundingBoxAscent/Descent
                    if (metrics.actualBoundingBoxAscent !== undefined && metrics.actualBoundingBoxDescent !== undefined) {
                        const actualHeight = metrics.actualBoundingBoxAscent + metrics.actualBoundingBoxDescent;

                        switch (baseline) {
                            case 'top':
                                drawY = y;
                                break;
                            case 'middle':
                                drawY = y + lineHeight / 2;
                                break;
                            case 'bottom':
                                drawY = y + lineHeight - (lineHeight - actualHeight) / 2 - metrics.actualBoundingBoxDescent;
                                break;
                            case 'alphabetic':
                                drawY = y + (lineHeight - actualHeight) / 2 + metrics.actualBoundingBoxAscent;
                                break;
                        }
                    } else {
                        // 降级方案：使用简单的 baseline 计算
                        switch (baseline) {
                            case 'top':
                                drawY = y;
                                break;
                            case 'middle':
                                drawY = y + lineHeight / 2;
                                break;
                            case 'bottom':
                                drawY = y + lineHeight;
                                break;
                            case 'alphabetic':
                                drawY = y + lineHeight * 0.8;
                                break;
                        }
                    }
                }

                this.ctx.fillText(char, xPosition, drawY);
                if (index < chars.length - 1) {
                    xPosition += this.ctx.measureText(char).width + letterSpacing;
                }
            });

            // 恢复原始对齐方式
            this.ctx.textAlign = originalAlign;

            // 计算实际宽度
            actualWidth = chars.reduce((sum, char, i) => {
                return sum + this.ctx.measureText(char).width + (i < chars.length - 1 ? letterSpacing : 0);
            }, 0);

            return { y: y + lineHeight, actualWidth, };
        }

        // 计算实际渲染的文本宽度
        actualWidth = this.ctx.measureText(text as string).width;

        // 精确计算 y 值，按传入 baseline 对齐
        let drawY = y;
        if (font && baseline && lineHeight) {
            const metrics = this.getTextMetrics(text, font, letterSpacing || 0);

            // Chrome 55 不支持 actualBoundingBoxAscent/Descent
            if (metrics.actualBoundingBoxAscent !== undefined && metrics.actualBoundingBoxDescent !== undefined) {
                const actualHeight = metrics.actualBoundingBoxAscent + metrics.actualBoundingBoxDescent;

                switch (baseline) {
                    case 'top':
                        drawY = y;
                        break;
                    case 'middle':
                        drawY = y + lineHeight / 2;
                        break;
                    case 'bottom':
                        drawY = y + lineHeight - (lineHeight - actualHeight) / 2 - metrics.actualBoundingBoxDescent;
                        break;
                    case 'alphabetic':
                        drawY = y + (lineHeight - actualHeight) / 2 + metrics.actualBoundingBoxAscent;
                        break;
                }
            } else {
                // 降级方案：使用简单的 baseline 计算
                switch (baseline) {
                    case 'top':
                        drawY = y;
                        break;
                    case 'middle':
                        drawY = y + lineHeight / 2;
                        break;
                    case 'bottom':
                        drawY = y + lineHeight;
                        break;
                    case 'alphabetic':
                        drawY = y + lineHeight * 0.8;
                        break;
                }
            }
        }

        this.ctx.fillText(text as string, x, drawY);
        return { y: y + lineHeight, actualWidth, };
    }

    // 绘制矩形
    fillRect({ fillStyle, gradient, x, y, width, height, isGroup, isXGroup, radius, }: IRect) {
        if (gradient) {
            const [x1, y1, x2, y2] = gradient.direction;
            const linearGradient = this.ctx.createLinearGradient(x1, y1, x2, y2);
            gradient.colorList.forEach(([offset, color]) => {
                linearGradient.addColorStop(offset, color);
            });
            this.ctx.fillStyle = linearGradient;
        } else {
            this.ctx.fillStyle = fillStyle;
        }
        const newY = isGroup ? this.groupY + y : y;
        const newX = isXGroup ? this.groupX + x : x;

        // 检查 radius 是否为数组
        if (Array.isArray(radius)) {
            // 确保有4个圆角参数（左上、右上、右下、左下）
            const [tl, tr, br, bl] = radius.concat([0, 0, 0, 0]).slice(0, 4); // 自动补零

            this.ctx.beginPath();

            // 左上角 → 右上角
            this.ctx.moveTo(newX + tl, newY);
            this.ctx.lineTo(newX + width - tr, newY);
            tr > 0 && this.ctx.arcTo(newX + width, newY, newX + width, newY + tr, tr);

            // 右上角 → 右下角
            this.ctx.lineTo(newX + width, newY + height - br);
            br > 0 && this.ctx.arcTo(newX + width, newY + height, newX + width - br, newY + height, br);

            // 右下角 → 左下角
            this.ctx.lineTo(newX + bl, newY + height);
            bl > 0 && this.ctx.arcTo(newX, newY + height, newX, newY + height - bl, bl);

            // 左下角 → 左上角
            this.ctx.lineTo(newX, newY + tl);
            tl > 0 && this.ctx.arcTo(newX, newY, newX + tl, newY, tl);

            this.ctx.closePath();
            this.ctx.fill();
        } else {
            // 处理单一半径的情况
            if (radius) {
                // 开始绘制圆角矩形
                this.ctx.beginPath();
                this.ctx.moveTo(newX + radius, newY);
                this.ctx.lineTo(newX + width - radius, newY);
                this.ctx.arcTo(newX + width, newY, newX + width, newY + radius, radius);
                this.ctx.lineTo(newX + width, newY + height - radius);
                this.ctx.arcTo(newX + width, newY + height, newX + width - radius, newY + height, radius);
                this.ctx.lineTo(newX + radius, newY + height);
                this.ctx.arcTo(newX, newY + height, newX, newY + height - radius, radius);
                this.ctx.lineTo(newX, newY + radius);
                this.ctx.arcTo(newX, newY, newX + radius, newY, radius);
                this.ctx.closePath();
                this.ctx.fill();
            } else {
                this.ctx.fillRect(newX, newY, width, height);
            }
        }
        isGroup && this.updateGroupInfo({ y: newY + height, });
        isXGroup && this.updateGroupInfo({ x: newX + width, });
    }

    private measureCharWidth(char: string, font: string, letterSpacing: number, isLast: boolean): number {
        this.ctx.font = font;
        if (this.supportsLetterSpacing) {
            (this.ctx as any).letterSpacing = `${letterSpacing}px`;
        }
        const w = this.ctx.measureText(char).width;
        if (!this.supportsLetterSpacing && letterSpacing && !isLast) {
            return w + letterSpacing;
        }
        return w;
    }

    drawMixedText({ segments, x, y, width, lineHeight, align = 'left', baseline = 'top', isGroup = false, isXGroup = false, ellipsis = false, suffix = '...', maxLines = 0, }: IMixedText) {
        const baseX = isXGroup ? this.groupX + x : x;
        const startY = isGroup ? this.groupY + y : y;

        type CharInfo = { char: string; font: string; fillStyle: string; letterSpacing: number; width: number; baseline?: CanvasTextBaseline };
        const charInfos: CharInfo[] = [];
        for (const seg of segments) {
            const spacing = seg.letterSpacing || 0;
            const chars = seg.text.split('');
            for (let i = 0; i < chars.length; i++) {
                const isLastOfAll = seg === segments[segments.length - 1] && i === chars.length - 1;
                const w = this.measureCharWidth(chars[i], seg.font, spacing, isLastOfAll);
                charInfos.push({ char: chars[i], font: seg.font, fillStyle: seg.fillStyle, letterSpacing: spacing, width: w, baseline: seg.baseline, });
            }
        }

        const lines: CharInfo[][] = [];
        let currentLine: CharInfo[] = [];
        let lineWidth_ = 0;

        for (const info of charInfos) {
            if (lineWidth_ + info.width > width && currentLine.length > 0) {
                lines.push(currentLine);
                currentLine = [info];
                lineWidth_ = info.width;
            } else {
                currentLine.push(info);
                lineWidth_ += info.width;
            }
        }
        if (currentLine.length > 0) {
            lines.push(currentLine);
        }

        if (ellipsis && maxLines > 0 && lines.length > maxLines) {
            lines.length = maxLines;
            const lastLine = lines[maxLines - 1];
            const lastSeg = segments[segments.length - 1];
            this.ctx.font = lastSeg.font;
            if (this.supportsLetterSpacing) {
                (this.ctx as any).letterSpacing = `${lastSeg.letterSpacing || 0}px`;
            }
            const suffixWidth = this.ctx.measureText(suffix).width;
            let lastLineWidth = lastLine.reduce((sum, c) => sum + c.width, 0);

            while (lastLineWidth + suffixWidth > width && lastLine.length > 0) {
                const removed = lastLine.pop()!;
                lastLineWidth -= removed.width;
            }
            const tailStyle = lastLine.length > 0 ? lastLine[lastLine.length - 1] : { font: lastSeg.font, fillStyle: lastSeg.fillStyle, letterSpacing: lastSeg.letterSpacing || 0, baseline: lastSeg.baseline, };
            for (const ch of suffix.split('')) {
                lastLine.push({
                    char: ch,
                    font: tailStyle.font,
                    fillStyle: tailStyle.fillStyle,
                    letterSpacing: tailStyle.letterSpacing,
                    baseline: tailStyle.baseline,
                    width: this.measureCharWidth(ch, tailStyle.font, tailStyle.letterSpacing, true),
                });
            }
        }

        const getLineWidth = (line: CharInfo[]) => line.reduce((sum, item) => sum + item.width, 0);

        let currentY = startY;
        let maxLineWidth = 0;
        for (const line of lines) {
            const lineWidth = getLineWidth(line);
            maxLineWidth = Math.max(maxLineWidth, lineWidth);

            let xPos = baseX;
            if (align === 'center') {
                xPos = baseX + (width - lineWidth) / 2;
            } else if (align === 'right') {
                xPos = baseX + (width - lineWidth);
            }
            this.ctx.textAlign = 'left';
            for (const { char, font, fillStyle, letterSpacing: spacing, width: charWidth, baseline: charBaseline, } of line) {
                const resolvedBaseline = charBaseline || baseline;
                this.ctx.font = font;
                this.ctx.fillStyle = fillStyle;
                if (this.supportsLetterSpacing) {
                    (this.ctx as any).letterSpacing = `${spacing}px`;
                }
                this.ctx.textBaseline = resolvedBaseline;
                const drawY = this.calculateTextY(char, font, currentY, lineHeight, resolvedBaseline, spacing);
                this.ctx.fillText(char, xPos, drawY);
                xPos += charWidth;
            }
            currentY += lineHeight;
        }

        const totalY = currentY;
        isGroup && this.updateGroupInfo({ y: totalY, });
        isXGroup && this.updateGroupInfo({ x: baseX + Math.max(width, maxLineWidth), });
        return totalY;
    }

    // 绘制线条
    drawLine({ strokeStyle, x1, y1, x2, y2, lineDash, lineWidth = 1, }: ILine) {
        this.ctx.strokeStyle = strokeStyle;
        this.ctx.lineWidth = lineWidth;

        // 设置虚线样式
        if (lineDash && lineDash.length > 0) {
            this.ctx.setLineDash(lineDash);
        } else {
            this.ctx.setLineDash([]); // 实线
        }

        this.ctx.beginPath();
        this.ctx.moveTo(x1, y1);
        this.ctx.lineTo(x2, y2);
        this.ctx.stroke();

        // 恢复默认设置
        this.ctx.setLineDash([]);
    }

    // 生成图片
    async createImage(imgType = 'png', maxWaitTime = 60000, quality?: number): Promise<string> {
        // 第一次调用时记录开始时间
        if (this.createImageStartTime === 0) {
            this.createImageStartTime = Date.now();
        }

        if (!this.isFinished) {
            // 检查是否超过最大等待时间
            if (Date.now() - this.createImageStartTime > maxWaitTime) {
                // 打印详细的超时信息
                const elapsedTime = Date.now() - this.createImageStartTime;
                const pendingSteps = this.drawingSteps.filter((step) => step.status === 'pending' || step.status === 'loading');
                const errorSteps = this.drawingSteps.filter((step) => step.status === 'error');
                const completedSteps = this.drawingSteps.filter((step) => step.status === 'completed');

                console.error('画布生成图片超时，详细信息:', {
                    超时时间: `${elapsedTime}ms`,
                    总步骤数: this.drawingSteps.length,
                    已完成: completedSteps.length,
                    进行中: pendingSteps.length,
                    失败: errorSteps.length,
                    isFinished: this.isFinished,
                });

                // 打印未完成的步骤
                if (pendingSteps.length > 0) {
                    console.error(
                        '未完成的绘制步骤:',
                        pendingSteps.map((step) => ({
                            索引: this.drawingSteps.indexOf(step),
                            类型: step.type,
                            状态: step.status,
                            已耗时: step.startTime ? `${Date.now() - step.startTime}ms` : '未知',
                            数据: step.data,
                        }))
                    );
                }

                // 打印失败的步骤
                if (errorSteps.length > 0) {
                    console.error(
                        '失败的绘制步骤:',
                        errorSteps.map((step) => ({
                            索引: this.drawingSteps.indexOf(step),
                            类型: step.type,
                            耗时: step.startTime && step.endTime ? `${step.endTime - step.startTime}ms` : '未知',
                            错误: step.error,
                            数据: step.data,
                        }))
                    );
                }

                // 打印耗时最长的步骤（前5个）
                const sortedSteps = [...this.drawingSteps]
                    .filter((step) => step.startTime && step.endTime)
                    .sort((a, b) => b.endTime! - b.startTime! - (a.endTime! - a.startTime!))
                    .slice(0, 5);

                if (sortedSteps.length > 0) {
                    console.warn(
                        '耗时最长的绘制步骤:',
                        sortedSteps.map((step) => ({
                            索引: this.drawingSteps.indexOf(step),
                            类型: step.type,
                            耗时: `${step.endTime! - step.startTime!}ms`,
                            数据: step.data,
                        }))
                    );
                }

                return Promise.reject(new Error(`画布生成图片超时 (${elapsedTime}ms)，已完成 ${completedSteps.length}/${this.drawingSteps.length} 步骤`));
            }

            await sleep(200);
            const _res = await this.createImage(imgType, maxWaitTime);
            return _res;
        }

        const totalTime = Date.now() - this.createImageStartTime;
        console.log(`画布生成完成，总耗时: ${totalTime}ms，总步骤数: ${this.drawingSteps.length}`);

        // 重置开始时间，以便下次调用
        this.createImageStartTime = 0;

        if ((imgType === 'jpeg' || imgType === 'webp') && typeof quality === 'number') {
            return this.canvas.toDataURL(`image/${imgType}`, quality);
        }
        return this.canvas.toDataURL(`image/${imgType}`);
    }
}
